import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, CheckCircle, AlertCircle, Loader2, ShieldCheck, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('hygad_token');

const TYPE_LABELS = {
  homestay: 'Homestay', hotel: 'Hotel', pg: 'PG', hostel: 'Hostel',
  farmhouse: 'Farmhouse', penthouse: 'Penthouse',
  function_hall: 'Function Hall', banquet_hall: 'Banquet Hall'
};

const ONBOARDING_FEES = {
  homestay: 799, hotel: 1499, pg: 999, hostel: 999,
  farmhouse: 1499, penthouse: 1499, function_hall: 1999, banquet_hall: 1999
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const PaymentModal = ({ isOpen, onClose, propertyType, propertyId, partnerName, partnerEmail, onSuccess }) => {
  const [step, setStep] = useState('confirm'); // confirm | processing | success | error | unconfigured
  const [errorMsg, setErrorMsg] = useState('');
  const fee = ONBOARDING_FEES[propertyType] || 999;

  useEffect(() => {
    if (isOpen) { setStep('confirm'); setErrorMsg(''); }
  }, [isOpen, propertyType]);

  const handlePayment = useCallback(async () => {
    setStep('processing');
    const loaded = await loadRazorpayScript();
    if (!loaded) { setErrorMsg('Could not load payment gateway. Check your internet connection.'); setStep('error'); return; }

    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ property_type: propertyType, property_id: propertyId })
      });
      const order = await res.json();
      if (!res.ok) {
        if (res.status === 503) { setStep('unconfigured'); return; }
        throw new Error(order.detail || 'Order creation failed');
      }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'HYGAD',
        description: `Partner Onboarding Fee — ${TYPE_LABELS[propertyType]}`,
        image: 'https://static.prod-images.emergentagent.com/jobs/6554b71a-e92e-45d6-a2ea-28b8d837c5dd/images/05ed22a83a162dbd4a68bdfa54453a7a8a9f16d8e3a633061831e14ecf8e4761.jpeg',
        prefill: { name: partnerName, email: partnerEmail },
        theme: { color: '#0A1628' },
        handler: async (response) => {
          setStep('processing');
          try {
            const verRes = await fetch(`${BACKEND_URL}/api/payments/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                property_type: propertyType,
                property_id: propertyId || null,
                amount: order.amount_inr
              })
            });
            const verData = await verRes.json();
            if (!verRes.ok) throw new Error(verData.detail || 'Verification failed');
            setStep('success');
            setTimeout(() => { onSuccess?.(verData); }, 2000);
          } catch (err) {
            setErrorMsg(err.message || 'Payment verification failed');
            setStep('error');
          }
        },
        modal: { ondismiss: () => { if (step === 'processing') setStep('confirm'); } }
      };
      new window.Razorpay(options).open();
    } catch (err) {
      setErrorMsg(err.message || 'Payment initialization failed');
      setStep('error');
    }
  }, [propertyType, propertyId, partnerName, partnerEmail]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          data-testid="payment-modal"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="relative bg-white rounded-3xl w-full max-w-md overflow-hidden z-10"
          style={{ boxShadow: '0 24px 80px rgba(10,22,40,0.2)' }}
        >
          {/* Header */}
          <div className="bg-navy px-8 pt-8 pb-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gold text-xs uppercase tracking-[0.2em] font-medium mb-2">One-Time Payment</p>
                <h2 className="font-serif text-2xl font-bold text-white">Partner Onboarding Fee</h2>
              </div>
              <button onClick={onClose} className="text-white/50 hover:text-white transition-colors mt-1">
                <X size={20} />
              </button>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-serif text-5xl font-black text-white">₹{fee.toLocaleString('en-IN')}</span>
              <span className="text-white/50 text-sm">one-time</span>
            </div>
            <p className="text-white/60 text-xs mt-2">{TYPE_LABELS[propertyType] || propertyType}</p>
          </div>

          {/* Body */}
          <div className="px-8 py-6">
            {step === 'confirm' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="text-sm text-gray-500 mb-5">
                  This is a one-time verification and onboarding fee to list your property on HYGAD.
                </p>
                <div className="space-y-2.5 mb-6">
                  {[
                    'Business Verification',
                    'Property Listing on HYGAD',
                    'Partner Dashboard Access',
                    'Direct Booking Enquiries',
                    'Analytics & Reviews',
                    'Priority Support'
                  ].map(b => (
                    <div key={b} className="flex items-center gap-2.5">
                      <CheckCircle size={14} className="text-gold shrink-0" />
                      <span className="text-sm text-gray-600">{b}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 mb-5">
                  <Lock size={12} />
                  <span>Secured by Razorpay. UPI, Cards, Net Banking accepted.</span>
                </div>
                <button
                  data-testid="pay-now-btn"
                  onClick={handlePayment}
                  className="w-full bg-navy hover:bg-navy-light text-white py-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <CreditCard size={16} />
                  Pay ₹{fee.toLocaleString('en-IN')} Now
                </button>
              </motion.div>
            )}

            {step === 'processing' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                <Loader2 size={36} className="text-gold animate-spin mx-auto mb-4" />
                <p className="font-medium text-navy">Processing payment...</p>
                <p className="text-gray-400 text-sm mt-1">Please do not close this window</p>
              </motion.div>
            )}

            {step === 'success' && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h3 className="font-serif text-xl font-bold text-navy mb-2">Payment Successful!</h3>
                <p className="text-gray-400 text-sm">Your onboarding fee has been received. Your property is now under review.</p>
              </motion.div>
            )}

            {step === 'error' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle size={28} className="text-red-500" />
                </div>
                <h3 className="font-medium text-navy mb-2">Payment Failed</h3>
                <p className="text-gray-400 text-sm mb-5">{errorMsg}</p>
                <button onClick={() => setStep('confirm')}
                  className="text-sm text-gold hover:underline font-medium">Try Again</button>
              </motion.div>
            )}

            {step === 'unconfigured' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck size={28} className="text-amber-500" />
                </div>
                <h3 className="font-medium text-navy mb-2">Razorpay Not Configured</h3>
                <p className="text-gray-500 text-sm mb-2">
                  To enable payments, add your Razorpay API keys to the backend environment:
                </p>
                <div className="bg-gray-900 rounded-xl p-4 text-left font-mono text-xs text-green-400 mb-4 text-left">
                  <p>RAZORPAY_KEY_ID=rzp_test_xxxx</p>
                  <p>RAZORPAY_KEY_SECRET=xxxx</p>
                </div>
                <p className="text-xs text-gray-400">Get keys at: <span className="text-gold">dashboard.razorpay.com</span></p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PaymentModal;
